import React, { useEffect, useState } from 'react';
import QuizProgress from './QuizProgress';
import QuizQuestion from './QuizQuestion';
import { XIcon } from 'lucide-react';
// Declare fbq as global function
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}
interface QuizProps {
  onComplete: (results: any) => void;
}
const Quiz: React.FC<QuizProps> = ({
  onComplete
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [showWarning, setShowWarning] = useState(false);
  useEffect(() => {
    // Track quiz start event
    if (window.fbq) {
      window.fbq('track', 'StartQuiz');
    }
  }, []);
  const questions = [{
    id: 'age',
    question: 'Hur gammal är du?',
    type: 'dropdown',
    options: [{
      value: 'under18',
      label: 'Under 18 år'
    }, {
      value: '18-24',
      label: '18-24 år'
    }, {
      value: '25-34',
      label: '25-34 år'
    }, {
      value: '35-44',
      label: '35-44 år'
    }, {
      value: '45-54',
      label: '45-54 år'
    }, {
      value: '55-65',
      label: '55-65 år'
    }, {
      value: 'over65',
      label: 'Över 65 år'
    }],
    required: true
  }, {
    id: 'erection_quality',
    question: 'Hur skulle du beskriva kvaliteten på dina erektioner?',
    type: 'scale',
    min: 1,
    max: 5,
    minLabel: 'Mycket svaga',
    maxLabel: 'Mycket starka',
    required: true
  }, {
    id: 'erection_duration',
    question: 'Hur ofta har du svårt att behålla erektionen under hela samlaget?',
    type: 'radio',
    options: [{
      value: 'always',
      label: 'Nästan alltid'
    }, {
      value: 'often',
      label: 'Ofta (mer än hälften av gångerna)'
    }, {
      value: 'sometimes',
      label: 'Ibland (ungefär hälften av gångerna)'
    }, {
      value: 'rarely',
      label: 'Sällan (mindre än hälften av gångerna)'
    }, {
      value: 'never',
      label: 'Nästan aldrig'
    }],
    required: true
  }, {
    id: 'libido',
    question: 'Hur skulle du beskriva din sexlust de senaste 3 månaderna?',
    type: 'scale',
    min: 1,
    max: 5,
    minLabel: 'Mycket låg',
    maxLabel: 'Mycket hög',
    required: true
  }, {
    id: 'energy',
    question: 'Hur ofta känner du att du saknar energi för sexuell aktivitet?',
    type: 'radio',
    options: [{
      value: 'always',
      label: 'Nästan alltid'
    }, {
      value: 'often',
      label: 'Ofta'
    }, {
      value: 'sometimes',
      label: 'Ibland'
    }, {
      value: 'rarely',
      label: 'Sällan'
    }, {
      value: 'never',
      label: 'Nästan aldrig'
    }],
    tooltip: 'VeeloBooster hjälper till att öka energi och uthållighet',
    required: true
  }, {
    id: 'sleep',
    question: 'Hur många timmar sömn får du vanligtvis per natt?',
    type: 'radio',
    options: [{
      value: 'less5',
      label: '5 timmar eller mindre'
    }, {
      value: '6-7',
      label: '6-7 timmar'
    }, {
      value: '7-8',
      label: '7-8 timmar'
    }, {
      value: 'more8',
      label: '8 timmar eller mer'
    }],
    required: true
  }, {
    id: 'stress',
    question: 'Hur skulle du beskriva din stressnivå i vardagen?',
    type: 'scale',
    min: 1,
    max: 5,
    minLabel: 'Mycket låg',
    maxLabel: 'Mycket hög',
    required: true
  }, {
    id: 'exercise',
    question: 'Hur ofta tränar du i veckan?',
    type: 'radio',
    options: [{
      value: 'none',
      label: 'Ingen träning'
    }, {
      value: '1-2',
      label: '1-2 pass i veckan'
    }, {
      value: '3-4',
      label: '3-4 pass i veckan'
    }, {
      value: '5plus',
      label: '5 eller fler pass i veckan'
    }],
    tooltip: 'Träning förbättrar blodcirkulation och kan stärka erektioner',
    required: true
  }, {
    id: 'satisfaction',
    question: 'Hur nöjd är du med ditt nuvarande sexliv?',
    type: 'scale',
    min: 1,
    max: 5,
    minLabel: 'Mycket missnöjd',
    maxLabel: 'Mycket nöjd',
    required: true
  }, {
    id: 'medical',
    question: 'Har en läkare rått dig att undvika vissa tillskott?',
    type: 'radio',
    options: [{
      value: 'yes',
      label: 'Ja'
    }, {
      value: 'no',
      label: 'Nej'
    }],
    required: true
  }];
  const handleAnswer = (questionId: string, answer: any) => {
    // Check if under 18
    if (questionId === 'age' && answer === 'under18') {
      setShowWarning(true);
      return;
    }
    // Check if medical warning is needed
    if (questionId === 'medical' && answer === 'yes') {
      calculateResults({
        ...answers,
        [questionId]: answer
      }, true);
      return;
    }
    const newAnswers = {
      ...answers,
      [questionId]: answer
    };
    setAnswers(newAnswers);
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
      // Track progress through quiz
      if (window.fbq) {
        window.fbq('track', 'QuizProgress', {
          step: currentStep + 1
        });
      }
    } else {
      calculateResults(newAnswers);
    }
  };
  const calculateResults = (finalAnswers: Record<string, any>, medicalWarning = false) => {
    // If medical warning, skip scoring and recommend medical consultation
    if (medicalWarning) {
      onComplete({
        profile: 'medical',
        title: 'Tala med vården',
        description: 'Baserat på dina svar rekommenderar vi att du konsulterar sjukvården innan du använder kosttillskott.',
        recommendations: ['Följ din läkares råd gällande kosttillskott', 'Vissa tillskott kan interagera med mediciner', 'Din hälsa kommer alltid först']
      });
      return;
    }
    // Calculate scores for each profile area
    let erectionScore = 0;
    let libidoScore = 0;
    let energyScore = 0;
    let balanceScore = 0;
    // Erection quality and duration affect erection score
    if (finalAnswers.erection_quality <= 2) {
      erectionScore += 2;
    } else if (finalAnswers.erection_quality === 3) {
      erectionScore += 1;
    }
    if (finalAnswers.erection_duration === 'always' || finalAnswers.erection_duration === 'often') {
      erectionScore += 2;
    } else if (finalAnswers.erection_duration === 'sometimes') {
      erectionScore += 1;
    }
    // Libido affects libido score
    if (finalAnswers.libido <= 2) {
      libidoScore += 2;
    } else if (finalAnswers.libido === 3) {
      libidoScore += 1;
    }
    // Energy, sleep and stress affect energy score
    if (finalAnswers.energy === 'always' || finalAnswers.energy === 'often') {
      energyScore += 2;
    } else if (finalAnswers.energy === 'sometimes') {
      energyScore += 1;
    }
    if (finalAnswers.sleep === 'less5' || finalAnswers.stress >= 4) {
      energyScore += 1;
    }
    // Exercise affects all scores
    if (finalAnswers.exercise === 'none') {
      erectionScore += 1;
      energyScore += 1;
    } else if (finalAnswers.exercise === '3-4' || finalAnswers.exercise === '5plus') {
      balanceScore += 1;
    }
    // Satisfaction affects which profile to prioritize
    if (finalAnswers.satisfaction <= 2) {
      // If very dissatisfied, boost all scores
      erectionScore += 1;
      libidoScore += 1;
      energyScore += 1;
    } else if (finalAnswers.satisfaction >= 4) {
      // If satisfied, boost balance for maintenance
      balanceScore += 2;
    }
    // Determine the highest score
    const scores = [{
      profile: 'erection',
      score: erectionScore
    }, {
      profile: 'libido',
      score: libidoScore
    }, {
      profile: 'energy',
      score: energyScore
    }, {
      profile: 'balance',
      score: balanceScore
    }];
    scores.sort((a, b) => b.score - a.score);
    const topProfile = scores[0].profile;
    // Track quiz completion
    if (window.fbq) {
      window.fbq('track', 'CompleteQuiz', {
        profile: topProfile
      });
    }
    // Map profile to result data
    const profileData = {
      erection: {
        profile: 'erection',
        title: 'Förbättrad Erektionskvalitet',
        description: 'Din plan: Starkare & långvarigare erektioner',
        recommendations: ['VeeloBooster ökar blodflödet till corpora cavernosa', 'Daglig lätt motion för bättre blodcirkulation', 'Minska alkoholkonsumtion som kan påverka erektionen', 'Fokusera på hjärtvänlig kost för bättre kärlhälsa']
      },
      libido: {
        profile: 'libido',
        title: 'Ökad Sexlust',
        description: 'Din plan: Högre libido & sexuell aptit',
        recommendations: ['VeeloBooster ökar testosteronnivåerna naturligt', 'Stresshantering för att minska kortisol som hämmar sexlusten', 'Tillräcklig sömn för hormonbalans', 'Näringsrik kost som stödjer hormonproduktion']
      },
      energy: {
        profile: 'energy',
        title: 'Mer Energi & Uthållighet',
        description: 'Din plan: Orka längre & prestera bättre',
        recommendations: ['VeeloBooster ger extra energi för bättre uthållighet', 'Förbättra sömnkvalitet för naturlig återhämtning', 'Stresshantering för att bevara energi', 'Gradvis öka konditionsträning för bättre uthållighet']
      },
      balance: {
        profile: 'balance',
        title: 'Sexuell Helhetsbalans',
        description: 'Din plan: Optimera din sexuella hälsa',
        recommendations: ['VeeloBooster stödjer både erektion, libido och energi', 'Balanserad livsstil för långsiktig sexuell hälsa', 'Kombination av styrka och kondition för optimal prestation', 'Fokusera på kommunikation och intimitet i relationen']
      },
      medical: {
        profile: 'medical',
        title: 'Tala med vården',
        description: 'Baserat på dina svar rekommenderar vi att du konsulterar sjukvården innan du använder kosttillskott.',
        recommendations: ['Följ din läkares råd gällande kosttillskott', 'Vissa tillskott kan interagera med mediciner', 'Din hälsa kommer alltid först']
      }
    };
    onComplete(profileData[topProfile]);
  };
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  return <section className="w-full min-h-screen py-8 px-4 bg-[#0A1020] flex flex-col items-center justify-center" data-id="element-220">
      <div className="max-w-2xl w-full bg-gray-900 rounded-xl p-6 md:p-8 relative" data-id="element-221">
        {showWarning ? <div className="text-center py-8" data-id="element-222">
            <h3 className="text-2xl font-bold mb-4" data-id="element-223">Åldersgräns</h3>
            <p className="text-gray-300 mb-6" data-id="element-224">
              Denna tjänst är endast avsedd för personer över 18 år.
            </p>
            <button onClick={() => window.location.reload()} className="px-6 py-3 bg-[#0EB7FF] text-black font-semibold rounded-md" data-id="element-225">
              Tillbaka till startsidan
            </button>
          </div> : <>
            <div className="absolute top-4 right-4" data-id="element-226">
              <button onClick={() => window.location.reload()} className="text-gray-400 hover:text-white" data-id="element-227">
                <XIcon className="w-5 h-5" data-id="element-228" />
              </button>
            </div>
            <div className="mb-8" data-id="element-229">
              <QuizProgress currentStep={currentStep} totalSteps={questions.length} data-id="element-230" />
            </div>
            <QuizQuestion question={questions[currentStep]} onAnswer={answer => handleAnswer(questions[currentStep].id, answer)} onBack={handleBack} showBack={currentStep > 0} data-id="element-231" />
          </>}
      </div>
    </section>;
};
export default Quiz;